import { useCallback } from 'react'
import {
  useGrowthBook,
  type Attributes,
  type FeatureResult,
} from '@growthbook/growthbook-react'

import { getEvaluator, hasRequiredAttributes } from '~/lib/featureFlags/helpers'

import type {
  FlagAttributeName,
  FlagAttributesArgs,
  FlagDefinition,
  FlagValue,
} from '~/lib/featureFlags/types'

const useFlagResult = (
  flag: FlagDefinition,
  attributes: Partial<Record<FlagAttributeName, unknown>> | undefined
): FeatureResult | null => {
  const growthbook = useGrowthBook()

  if (!growthbook.ready || !hasRequiredAttributes(flag, attributes)) {
    return null
  }

  const result = getEvaluator(growthbook.getDecryptedPayload()).evalFeature(flag.key, {
    attributes: { ...growthbook.getAttributes(), ...attributes },
  })

  return result.value === null ? null : result
}

export const useFeatureFlagEnabled = <
  TFlag extends FlagDefinition<boolean, FlagAttributeName>
>(
  flag: TFlag,
  ...[attributes]: FlagAttributesArgs<TFlag>
): boolean => {
  const result = useFlagResult(flag, attributes)

  return result ? result.on : flag.default
}

export const useFeatureFlagValue = <TFlag extends FlagDefinition>(
  flag: TFlag,
  ...[attributes]: FlagAttributesArgs<TFlag>
): FlagValue<TFlag> => {
  const result = useFlagResult(flag, attributes)

  return (result ? result.value : flag.default) as FlagValue<TFlag>
}

export const useIdentifyFeatureFlagUser = (): ((attributes: Attributes) => void) => {
  const growthbook = useGrowthBook()

  return useCallback(
    (attributes: Attributes) => {
      growthbook.setAttributes({ ...growthbook.getAttributes(), ...attributes })
    },
    [growthbook]
  )
}
